import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import type { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { saveAttribution } from '@/utils/attribution';
import { prepareSignupJourney, settleSignupJourney } from '@/services/signupJourney';
import {
  beginAuthAttempt,
  captureAuthOutcome,
  gateAuthAttempt,
  noteAuthLogout,
  noteAuthSuccess,
  noteSignupCandidate,
  type AuthAttempt,
} from '@/services/posthogAnalytics';
import { useStore } from '@/store/useStore';
import { reportError } from '@/services/errorTracking';
import { validatePassword } from '@/utils/passwordValidation';
import { randomUUID } from '@/utils/uuid';
import { invalidateUserIdCache } from '@/services/userIdCache';
import { FLAGS } from '@/config/featureFlags';
import { buildPendingTryDraft } from '@/utils/anonymousSession';
import { getNativeMirror } from '@/lib/native/localStorageMirror';
import {
  BRIDGE_EXISTING_SESSION_COPY,
  BRIDGE_FAILED_COPY,
  isEmailSignupCommitting,
  noteUserAuthAction,
  noteAuthActorAdoption,
  runEmailSignupBridge,
  hasRecoveryCallback,
  getAdoptedRecoveryUserId,
  getAuthActionEpoch,
} from '@/services/emailSignupBridge';

export type RecoveryStatus =
  'none' | 'checking' | 'ready' | 'failed' | 'conflict' | 'superseded' | 'invalid';

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  recoveryStatus: RecoveryStatus;
  recoveryUserId: string | null;
  signIn: (
    email: string,
    password: string,
    rememberMe?: boolean,
  ) => Promise<{ error: Error | null }>;
  signUp: (
    email: string,
    password: string,
    name?: string,
    lifecycleOptIn?: boolean,
  ) => Promise<{ error: Error | null }>;
  signInWithGoogle: () => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
  updatePassword: (
    newPassword: string,
    expectedUserId?: string,
  ) => Promise<{ error: Error | null }>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** Anti-enumeration copy from `supabase/functions/login`. */
const LOGIN_GENERIC_ERROR = 'Invalid email or password.';
/**
 * Set (with a timestamp) before a real Google start in this tab, so the return trip can be
 * counted and a crafted URL cannot. It expires because a provider round trip is short: an old
 * marker must never turn a later unrelated error URL into a "cancel".
 */
/** Shown if a manual auth action lands in the short window where an email link is being adopted. */
const BRIDGE_BUSY_COPY = 'Finishing your email sign-in. Try again in a moment.';
const GOOGLE_STARTED_KEY = 'vidi-auth-google-started';
const GOOGLE_STARTED_TTL_MS = 10 * 60 * 1000;

// Outcome bookkeeping is best effort: a denied storage, a missing UUID source or an analytics
// failure must never turn a successful auth into an error or reject the product action.
/** The photos chosen in /try, sent with signup so confirming in another browser keeps them. */
function selectedDraftForSignup(): Record<string, unknown> | undefined {
  try {
    return buildPendingTryDraft() ?? undefined;
  } catch {
    return undefined;
  }
}

function bestEffort(fn: () => void): void {
  try {
    fn();
  } catch {
    /* measurement only */
  }
}
/** One id per stage of one submission (attempt, result); a retry draws new ones. */
function newStageId(): string | undefined {
  try {
    return randomUUID();
  } catch {
    return undefined;
  }
}
type AuthAction = 'signup' | 'login' | 'provider';
type AuthMethod = 'email' | 'google';
/**
 * Snapshot consent + actor BEFORE the request is awaited; every terminal below carries it, so a
 * late error/confirmation/success cannot be counted after a consent change or account switch.
 * null (also when bookkeeping throws) means "do not measure"; the auth action itself is unaffected.
 */
const startAttempt = (): AuthAttempt | null => {
  try {
    return beginAuthAttempt();
  } catch {
    return null;
  }
};
/** Null (unmeasured) when ownership already moved, or when bookkeeping throws. */
const gateAttempt = (attempt: AuthAttempt | null): AuthAttempt | null => {
  try {
    return gateAuthAttempt(attempt);
  } catch {
    return null;
  }
};
const reportAuth = (
  action: AuthAction,
  method: AuthMethod,
  outcome: 'attempt' | 'confirmation_required' | 'error' | 'cancelled',
  stageId?: string,
  attempt?: AuthAttempt | null,
) =>
  bestEffort(() =>
    // Callback returns have no attempt: keep the original 4-argument call for them.
    attempt === undefined
      ? captureAuthOutcome(action, method, outcome, stageId)
      : captureAuthOutcome(action, method, outcome, stageId, attempt),
  );
/** Success is handed over by the identity the auth call actually returned, then verification-gated. */
const reportAuthSuccess = (
  userId: unknown,
  action: AuthAction,
  method: AuthMethod,
  stageId?: string,
  attempt?: AuthAttempt | null,
) =>
  bestEffort(() => {
    if (typeof userId !== 'string' || !userId) return;
    if (attempt === undefined) noteAuthSuccess(userId, action, method, stageId);
    else noteAuthSuccess(userId, action, method, stageId, attempt);
  });
const markGoogleStarted = () =>
  bestEffort(() => sessionStorage.setItem(GOOGLE_STARTED_KEY, String(Date.now())));
const clearGoogleMarker = () => bestEffort(() => sessionStorage.removeItem(GOOGLE_STARTED_KEY));
/** The only route Google returns to (signInWithGoogle's redirectTo); any other callback is unrelated. */
const GOOGLE_RETURN_PATH = '/';
/** Identity evidence that the returned session really came from Google; anything else is ambiguous. */
function hasGoogleIdentity(user: User | null | undefined): boolean {
  const meta = user?.app_metadata as { provider?: unknown; providers?: unknown } | undefined;
  return (
    meta?.provider === 'google' ||
    (Array.isArray(meta?.providers) && meta.providers.includes('google'))
  );
}
/** Consumes the marker once, so remounts/StrictMode/replays of one callback count once. */
function consumeGoogleMarker(): boolean {
  try {
    const raw = sessionStorage.getItem(GOOGLE_STARTED_KEY);
    if (raw === null) return false;
    sessionStorage.removeItem(GOOGLE_STARTED_KEY);
    const age = Date.now() - Number(raw);
    return Number.isFinite(age) && age >= 0 && age <= GOOGLE_STARTED_TTL_MS;
  } catch {
    return false;
  }
}

type FunctionsInvokeError = {
  message: string;
  context?: { status?: number; json?: () => Promise<unknown> };
};

/**
 * supabase.functions.invoke puts non-2xx JSON on FunctionsHttpError.context
 * (a Response). `error.message` is only "Edge Function returned a non-2xx
 * status code", which is not the login function's body and is not what
 * callers (or the auth e2e) should display.
 */
async function messageFromFunctionsInvoke(
  error: FunctionsInvokeError,
  fallback: string,
): Promise<string> {
  const ctx = error.context;
  if (ctx && typeof ctx.json === 'function') {
    try {
      const body: unknown = await ctx.json();
      if (
        body !== null &&
        typeof body === 'object' &&
        'error' in body &&
        typeof (body as { error: unknown }).error === 'string' &&
        (body as { error: string }).error.trim() !== ''
      ) {
        return (body as { error: string }).error;
      }
    } catch {
      // Body is not JSON or was already consumed.
    }
  }
  if (ctx?.status === 401) return fallback;
  return fallback;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [recoveryStatus, setRecoveryStatus] = useState<RecoveryStatus>(() => {
    if (window.location.pathname !== '/reset-password') return 'none';
    return hasRecoveryCallback() || new URLSearchParams(window.location.search).get('code')
      ? 'checking'
      : 'invalid';
  });
  const [recoveryUserId, setRecoveryUserId] = useState<string | null>(null);
  // Credential-free fallback for an email confirmation link that could not sign the learner in.
  const [callbackNotice, setCallbackNotice] = useState<string | null>(null);

  useEffect(() => {
    const recoveryRoute = window.location.pathname === '/reset-password';
    const recoveryCallback = hasRecoveryCallback();
    // Web email link (implicit fragment the PKCE client refuses). Runs outside
    // onAuthStateChange; success flows through that existing path like any other sign-in.
    void runEmailSignupBridge(supabase)
      .then((outcome) => {
        if (recoveryCallback) {
          const id = getAdoptedRecoveryUserId();
          setRecoveryUserId(outcome === 'adopted' ? id : null);
          setRecoveryStatus(
            outcome === 'adopted' && id
              ? 'ready'
              : outcome === 'existing_session'
                ? 'conflict'
                : outcome === 'superseded'
                  ? 'superseded'
                  : 'failed',
          );
          return;
        }
        if (outcome === 'failed') {
          setCallbackNotice(BRIDGE_FAILED_COPY);
          reportError(new Error('email signup callback could not be adopted'), {
            operation: 'emailSignupCallback',
          });
        } else if (outcome === 'existing_session') setCallbackNotice(BRIDGE_EXISTING_SESSION_COPY);
      })
      .catch(() => {
        if (recoveryCallback) setRecoveryStatus('failed');
        else setCallbackNotice(BRIDGE_FAILED_COPY);
      });

    const params = new URLSearchParams(window.location.search);
    // Provider return: only counted when this tab actually started Google, so a crafted URL
    // cannot fabricate outcomes. Only access_denied is a cancel; the raw error text is never read.
    const code = params.get('code');
    let returnedError: string | null = null;
    bestEffort(() => {
      returnedError =
        params.get('error') ?? new URLSearchParams(window.location.hash.slice(1)).get('error');
    });
    // Any callback consumes the marker; only one on Google's own return route can be counted.
    const consumed = code || returnedError ? consumeGoogleMarker() : false;
    const startedGoogle = consumed && window.location.pathname === GOOGLE_RETURN_PATH;
    const callbackId = startedGoogle ? newStageId() : undefined;
    if (startedGoogle && returnedError)
      reportAuth(
        'provider',
        'google',
        returnedError === 'access_denied' ? 'cancelled' : 'error',
        callbackId,
      );
    const measureExchange = (error: unknown, returned: Session | null | undefined) => {
      // A callback that already reported its error is one outcome, not two.
      if (!startedGoogle || returnedError) return;
      if (error) reportAuth('provider', 'google', 'error', callbackId);
      else if (returned && hasGoogleIdentity(returned.user))
        reportAuthSuccess(returned.user.id, 'provider', 'google', callbackId);
      // A session without Google identity evidence is ambiguous and stays unmeasured.
    };
    if (code && !FLAGS.nativeIOS) {
      // The web client (createBrowserClient, detectSessionInUrl) ALREADY exchanges a PKCE code
      // for this browser's own verifier while it initializes, and that code is single-use: a
      // second explicit exchange here would fail after a genuine sign-in (the verifier is gone)
      // and report a false error. So read the SDK's own initialization result instead.
      // initialize() returns the in-flight initialization; the session reaches state through
      // onAuthStateChange (SIGNED_IN / INITIAL_SESSION) below, exactly as before.
      void supabase.auth
        .initialize()
        .then(async ({ error }) => {
          if (error) {
            if (recoveryRoute) setRecoveryStatus('failed');
            measureExchange(error, null);
            reportError(error, { operation: 'exchangeCodeForSession' });
            return;
          }
          // The SDK removes `code` only after its own exchange succeeded. Still present means it
          // did not treat this as this browser's PKCE callback (no verifier here, e.g. another
          // browser): keep the old explicit exchange so that failure is still reported, but
          // never measure it, because nothing ties it to a Google start in this tab.
          if (new URLSearchParams(window.location.search).get('code') === code) {
            const { data, error: lateError } = await supabase.auth.exchangeCodeForSession(code);
            if (!lateError && data.session)
              window.history.replaceState({}, '', window.location.pathname);
            else if (lateError) reportError(lateError, { operation: 'exchangeCodeForSession' });
            if (recoveryRoute)
              setRecoveryStatus((current) => (current === 'ready' ? current : 'failed'));
            return;
          }
          const { data } = await supabase.auth.getSession();
          measureExchange(null, data.session);
          if (recoveryRoute)
            setRecoveryStatus((current) => (current === 'ready' ? current : 'invalid'));
          // Clean the rest of the callback query, as the explicit exchange used to.
          window.history.replaceState({}, '', window.location.pathname);
        })
        .catch((err) => {
          if (recoveryRoute) setRecoveryStatus('failed');
          measureExchange(err, null);
          reportError(err, { operation: 'exchangeCodeForSession' });
        });
    } else if (code) {
      // Native client: detectSessionInUrl is false, so this explicit exchange is the only one.
      // void + .catch: a rejected exchange (network blip, expired code) used
      // to bubble out as an unhandled rejection and disappear into the void.
      // Now it lands in Sentry so OAuth failures are visible.
      void supabase.auth
        .exchangeCodeForSession(code)
        .then(({ data, error }) => {
          measureExchange(error ?? (data.session ? null : 'no session'), data.session);
          if (!error && data.session) {
            // Clean the code from the URL to prevent re-exchange on refresh
            window.history.replaceState({}, '', window.location.pathname);
          } else if (error) {
            reportError(error, { operation: 'exchangeCodeForSession' });
          }
        })
        .catch((err) => {
          measureExchange(err, null);
          reportError(err, { operation: 'exchangeCodeForSession' });
        });
    }

    // Use onAuthStateChange as the sole source of truth for session state.
    // undefined = nothing adopted yet, so the first event always drops any cache warmed earlier.
    let lastAdoptedUserId: string | null | undefined;
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      // Synchronous only (this callback runs inside the SDK's auth lock, never await here).
      // A direct SIGNED_IN / token / session adoption of a DIFFERENT account arrives without a
      // SIGNED_OUT, so the actor cache is dropped whenever the adopted UID changes.
      const adoptedUserId = session?.user?.id ?? null;
      if (event === 'SIGNED_OUT' || adoptedUserId !== lastAdoptedUserId) {
        invalidateUserIdCache();
        if (event === 'SIGNED_OUT' || adoptedUserId) noteAuthActorAdoption();
      }
      // A direct account switch must also discard the previous account's loaded cards.
      // Initial guest adoption keeps the local Try draft; SIGNED_OUT has its cleanup below.
      if (lastAdoptedUserId && adoptedUserId && adoptedUserId !== lastAdoptedUserId) {
        useStore.getState().resetStore();
      }
      lastAdoptedUserId = adoptedUserId;
      setSession(session);
      setUser(session?.user ?? null);
      setRecoveryUserId((previous) => (previous === adoptedUserId ? previous : null));
      if (recoveryRoute && event === 'PASSWORD_RECOVERY' && session?.user) {
        setRecoveryUserId(session.user.id);
        setRecoveryStatus('ready');
      }
      // A password-reset return is never a Google return.
      if (event === 'PASSWORD_RECOVERY') clearGoogleMarker();

      // Stop showing the loading screen on any definitive auth event.
      // INITIAL_SESSION fires on mount; SIGNED_IN fires after OAuth callback.
      if (
        event === 'INITIAL_SESSION' ||
        event === 'SIGNED_IN' ||
        event === 'SIGNED_OUT' ||
        event === 'TOKEN_REFRESHED' ||
        event === 'PASSWORD_RECOVERY'
      ) {
        setIsLoading(false);
      }

      // "Remember Me" session-only logic:
      // If the user previously signed in without "Remember Me", their session
      // should not survive a browser close. We detect a new browser session by
      // checking sessionStorage (cleared on browser close). If the flag is set
      // in localStorage and this is a new browser session, sign out immediately.
      //
      // Native (plan §2.7) is exempt: iOS reaps the webview between launches,
      // so sessionStorage is empty on EVERY cold start and this heuristic would
      // read that as "browser closed" and sign the user out each time they open
      // the app. DesignLogin never sets `vidi-session-only` on native either,
      // so the flag should not exist there — this guard is the second line.
      if (!FLAGS.nativeIOS && event === 'INITIAL_SESSION' && session?.user) {
        const isNewBrowserSession = !sessionStorage.getItem('vidi-browser-session');
        const sessionOnly = localStorage.getItem('vidi-session-only') === '1';
        if (isNewBrowserSession && sessionOnly) {
          void supabase.auth.signOut();
        } else {
          sessionStorage.setItem('vidi-browser-session', '1');
        }
      }

      // Reset Zustand store + clear sensitive session data on sign-out
      if (event === 'SIGNED_OUT') {
        useStore.getState().resetStore();
        // The web Try draft belongs to this browser, not the loaded account. Keep it and
        // tab-local Create/feedback drafts. Native keeps its existing cold-launch cleanup.
        if (FLAGS.nativeIOS) {
          localStorage.removeItem('vidi-anonymous-session');
          getNativeMirror()?.remove('vidi-anonymous-session');
        }
        localStorage.removeItem('vidi-guides-completed');
        localStorage.removeItem('vidi-checklist-dismissed');
        localStorage.removeItem('vidi-session-only');
        sessionStorage.removeItem('vidi-browser-session');
        clearGoogleMarker();
      }

      // Save attribution for new signups (fire and forget, idempotent)
      if (session?.user) {
        saveAttribution(session.user.id).catch(() => {});
      }

      // The /try → account migration is NOT started from here any more. This
      // callback runs inside Supabase's auth lock, so the migration's own auth
      // calls (getUser, token refresh) could not complete until it returned,
      // and it raced DataLoader's first load (which then read default
      // settings and bounced the account to /onboarding). DataLoader now runs
      // it once, before loadFromApi, where those calls are safe.
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = useCallback(async (email: string, password: string, rememberMe = false) => {
    if (isEmailSignupCommitting()) return { error: new Error(BRIDGE_BUSY_COPY) };
    noteUserAuthAction();
    // Outcomes are finite labels only: raw errors never reach analytics, and a success is
    // reported after this account's trusted verification (see noteAuthSuccess).
    // A new non-Google flow: an abandoned Google start must not claim its callback.
    clearGoogleMarker();
    const attempt = startAttempt();
    const attemptId = newStageId();
    const resultId = newStageId();
    reportAuth('login', 'email', 'attempt', attemptId, attempt);
    const fail = (error: Error) => {
      reportAuth('login', 'email', 'error', resultId, attempt);
      return { error };
    };
    try {
      // Route through the login edge function so IP rate limits and uniform
      // error messages apply. Calling GoTrue from the browser bypasses that
      // proxy (the signup path already learned this lesson).
      const { data, error } = await supabase.functions.invoke('login', {
        body: { email, password },
      });
      if (error) {
        const message = await messageFromFunctionsInvoke(error, LOGIN_GENERIC_ERROR);
        return fail(new Error(message));
      }
      if (!data?.session) {
        return fail(new Error('Login failed'));
      }
      // Ownership is judged BEFORE the session is adopted: adoption itself changes the actor.
      const successAttempt = gateAttempt(attempt);
      const { data: set, error: sessionError } = await supabase.auth.setSession({
        access_token: data.session.access_token,
        refresh_token: data.session.refresh_token,
      });
      if (sessionError) {
        return fail(new Error(sessionError.message));
      }
      if (rememberMe) {
        localStorage.removeItem('vidi-session-only');
      } else {
        localStorage.setItem('vidi-session-only', '1');
        // INITIAL_SESSION only sets this marker for an already restored user.
        // A fresh session-only login must mark this tab before its first reload.
        if (!FLAGS.nativeIOS) sessionStorage.setItem('vidi-browser-session', '1');
      }
      reportAuthSuccess(
        set?.session?.user?.id ?? data.session.user?.id,
        'login',
        'email',
        resultId,
        successAttempt,
      );
      return { error: null };
    } catch (err) {
      reportAuth('login', 'email', 'error', resultId, attempt);
      throw err;
    }
  }, []);

  const signUp = useCallback(
    async (email: string, password: string, name?: string, lifecycleOptIn = false) => {
      if (isEmailSignupCommitting()) return { error: new Error(BRIDGE_BUSY_COPY) };
      noteUserAuthAction();
      const validation = validatePassword(password);
      if (!validation.valid) {
        return { error: new Error(validation.error!) };
      }

      clearGoogleMarker();
      const attempt = startAttempt();
      const attemptId = newStageId();
      const resultId = newStageId();
      reportAuth('signup', 'email', 'attempt', attemptId, attempt);
      try {
        // Route through edge function for server-side rate limiting + validation.
        // The essential body is built FIRST (the draft read can throw), and the optional analytics
        // context is prepared last, so nothing can throw between prepare and the settle below.
        const essentialBody = {
          email,
          password,
          name,
          lifecycleOptIn,
          tryDraft: selectedDraftForSignup(),
        };
        // Optional analytics context (null unless consented, SDK ready, anonymous and the bounded
        // revocation queue has room): it never waits for analytics and never changes the response.
        const journey = prepareSignupJourney();
        // The server copy is stored before the edge function answers, so the withdrawal capability
        // is settled exactly once when the request answers or fails (including a synchronous
        // failure of invoke itself), bound to the consent period it was sent in.
        const settle = (userId: unknown) => {
          if (journey) {
            bestEffort(() =>
              settleSignupJourney((fn, args) => supabase.rpc(fn, args), journey, userId),
            );
          }
        };
        let invoked;
        try {
          invoked = supabase.functions.invoke('signup', {
            body: journey ? { ...essentialBody, journey: journey.body } : essentialBody,
          });
        } catch (failure) {
          settle(undefined);
          throw failure;
        }
        const { data, error } = await invoked.then(
          (result) => {
            settle(result.error ? undefined : result.data?.user?.id);
            return result;
          },
          (failure: unknown) => {
            settle(undefined);
            throw failure;
          },
        );
        if (error) {
          reportAuth('signup', 'email', 'error', resultId, attempt);
          return { error: new Error(error.message) };
        }
        // A returned user may be obfuscated for an existing email. The candidate only
        // becomes a signup event after getUser verifies the same new account.
        bestEffort(() =>
          noteSignupCandidate(typeof data?.user?.id === 'string' ? data.user.id : undefined),
        );
        // Edge function returns { user, session } — trigger auth state change by
        // setting the session explicitly so onAuthStateChange fires and the UI
        // transitions to the confirmation screen.
        if (data?.session) {
          const successAttempt = gateAttempt(attempt);
          const { data: set, error: sessionError } = await supabase.auth.setSession({
            access_token: data.session.access_token,
            refresh_token: data.session.refresh_token,
          });
          // A returned setSession failure is a failed signup, not a success or a false UI success.
          if (sessionError) {
            reportAuth('signup', 'email', 'error', resultId, attempt);
            return { error: new Error(sessionError.message) };
          }
          reportAuthSuccess(
            set?.session?.user?.id ?? data.session.user?.id,
            'signup',
            'email',
            resultId,
            successAttempt,
          );
        } else {
          // Accepted with no session. This is NOT proof that mail was delivered or that the
          // account is new (an existing email returns the same shape).
          reportAuth('signup', 'email', 'confirmation_required', resultId, attempt);
        }
        return { error: null };
      } catch (err) {
        reportAuth('signup', 'email', 'error', resultId, attempt);
        throw err;
      }
    },
    [],
  );

  const signInWithGoogle = useCallback(async () => {
    if (isEmailSignupCommitting()) return { error: new Error(BRIDGE_BUSY_COPY) };
    noteUserAuthAction();
    // Return to wherever the app is actually running (prod, a Vercel preview,
    // or localhost) so OAuth doesn't bounce previews to production. Fall back
    // to VITE_SITE_URL only for non-routable hosts (e.g. a Docker LAN IP like
    // 192.168.x.x) that Supabase's redirect allowlist won't recognize.
    const origin = window.location.origin;
    const isLanIp = /^https?:\/\/(\d{1,3}\.){3}\d{1,3}(:\d+)?$/.test(origin);
    const siteUrl = isLanIp ? import.meta.env.VITE_SITE_URL || origin : origin;
    const attempt = startAttempt();
    const attemptId = newStageId();
    const resultId = newStageId();
    reportAuth('provider', 'google', 'attempt', attemptId, attempt);
    // Marker first: signInWithOAuth can navigate away before it ever returns.
    markGoogleStarted();
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: { redirectTo: `${siteUrl}/` },
      });
      if (error) {
        clearGoogleMarker();
        reportAuth('provider', 'google', 'error', resultId, attempt);
      }
      return { error };
    } catch (err) {
      clearGoogleMarker();
      reportAuth('provider', 'google', 'error', resultId, attempt);
      throw err;
    }
  }, []);

  const signOut = useCallback(async () => {
    if (isEmailSignupCommitting()) throw new Error(BRIDGE_BUSY_COPY);
    // Logout ends any in-flight attempt's ownership even when the anonymous actor stays null.
    bestEffort(() => noteAuthLogout());
    noteUserAuthAction();
    clearGoogleMarker();
    const { error } = await supabase.auth.signOut({ scope: 'local' });
    if (error) throw error;
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    clearGoogleMarker();
    // Route through edge function for rate limiting + enumeration-safe response.
    const { error } = await supabase.functions.invoke('reset-password', {
      body: { email },
    });
    return { error };
  }, []);

  const updatePassword = useCallback(async (newPassword: string, expectedUserId?: string) => {
    const validation = validatePassword(newPassword);
    if (!validation.valid) {
      return { error: new Error(validation.error!) };
    }

    // A recovery form is tied to the adopted actor. Pin its SDK session for the request so
    // a concurrent account switch cannot update a different account's password.
    let headers: Record<string, string> | undefined;
    if (expectedUserId) {
      const epoch = getAuthActionEpoch();
      const { data, error } = await supabase.auth.getSession();
      if (error || data.session?.user.id !== expectedUserId || getAuthActionEpoch() !== epoch) {
        return { error: new Error('RECOVERY_CHANGED') };
      }
      headers = { Authorization: `Bearer ${data.session.access_token}` };
    }
    // Route through the existing edge function for server-side validation.
    const { error } = await supabase.functions.invoke('update-password', {
      body: { password: newPassword },
      ...(headers ? { headers } : {}),
    });

    if (error) {
      // The edge function's verifyAuth() (supabase/functions/_shared/auth.ts)
      // is the only source of a 401 from update-password, and it returns 401
      // exactly when the recovery session's token is invalid or expired —
      // e.g. the user took too long typing a new password after opening the
      // reset link. supabase-js surfaces this as a FunctionsHttpError whose
      // `.context` is the raw Response, so `.status` is readable without an
      // extra fetch to parse the JSON body. Callers use this sentinel to
      // route the user to "request a new link" instead of a dead-end
      // generic failure message.
      const status = (error as { context?: { status?: number } }).context?.status;
      if (status === 401) {
        return { error: new Error('TOKEN_EXPIRED') };
      }
      return { error: new Error(error.message) };
    }

    return { error: null };
  }, []);

  const value: AuthContextValue = {
    user,
    session,
    isLoading,
    recoveryStatus,
    recoveryUserId,
    signIn,
    signUp,
    signInWithGoogle,
    signOut,
    resetPassword,
    updatePassword,
  };

  return (
    <AuthContext.Provider value={value}>
      {callbackNotice && (
        <div
          role="alert"
          className="rd"
          style={{
            position: 'fixed',
            top: 16,
            left: 16,
            right: 16,
            maxWidth: 720,
            margin: '0 auto',
            zIndex: 2147483000,
            padding: '12px 16px',
            textAlign: 'center',
            background: 'var(--stage-fill, var(--bg))',
            color: 'var(--stage-fg, var(--text-primary))',
            border: '1px solid var(--stage-line, var(--border))',
            borderRadius: 'var(--radius-panel, 10px)',
          }}
        >
          {callbackNotice}{' '}
          {callbackNotice === BRIDGE_FAILED_COPY && (
            <a
              href="/login"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                minHeight: 44,
                padding: '0 8px',
                textDecoration: 'underline',
              }}
            >
              Sign in
            </a>
          )}
          <button
            type="button"
            onClick={() => setCallbackNotice(null)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              minHeight: 44,
              padding: '0 8px',
              textDecoration: 'underline',
            }}
          >
            Dismiss
          </button>
        </div>
      )}
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
